import { useMutation, useQueryClient, type QueryKey, type UseMutationOptions } from '@tanstack/react-query';

/**
 * What a screen may add to a mutation hook: what to do once it finishes — close
 * a dialog, show a toast, open the new site. Which cached data the change makes
 * stale is the hook's business, not the screen's.
 */
export type MutationCallbacks<TData, TVariables> = Pick<
  UseMutationOptions<TData, Error, TVariables>,
  'onSuccess' | 'onError' | 'onSettled'
>;

/**
 * A mutation that marks what it changed as stale before the screen's own
 * `onSuccess` runs. The screens showing that data fetch it again in the
 * background; nothing is refetched by hand.
 */
export function useApiMutation<TData, TVariables = void>(
  mutationFn: (variables: TVariables) => Promise<TData>,
  stale: (data: TData, variables: TVariables) => readonly QueryKey[],
  callbacks: MutationCallbacks<TData, TVariables> = {},
) {
  const client = useQueryClient();
  return useMutation<TData, Error, TVariables>({
    ...callbacks,
    mutationFn,
    onSuccess: (...args) => {
      for (const queryKey of stale(args[0], args[1])) void client.invalidateQueries({ queryKey });
      return callbacks.onSuccess?.(...args);
    },
  });
}
